import { inject } from '@angular/core';
import { CanActivateFn, Routes } from '@angular/router';
import { Router } from '@angular/router';

import { MAINTENANCE_CONFIG } from './config/maintenance.config';
import { authGuard, publicGuard, routeAccessGuard } from './core/auth/auth.guard';
import { AuthService } from './core/auth/auth.service';
import { AppRoute } from './core/auth/constants/group-access.constants';

// Redirects to /maintenance when maintenance mode is active
const maintenanceGuard: CanActivateFn = () => {
  if (MAINTENANCE_CONFIG.enabled) {
    return inject(Router).createUrlTree(['/maintenance']);
  }
  return true;
};

// Redirects to the user's default (first allowed) section, honoring maintenance mode.
// Used for the root / fallback paths so each group lands on a section it can access.
const defaultRedirectGuard: CanActivateFn = () => {
  const router = inject(Router);
  if (MAINTENANCE_CONFIG.enabled) {
    return router.createUrlTree(['/maintenance']);
  }
  return router.createUrlTree([inject(AuthService).getDefaultRoute()]);
};

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./layouts/main-layout.component').then(
        (m: { MainLayoutComponent: typeof import('./layouts/main-layout.component').MainLayoutComponent }) =>
          m.MainLayoutComponent
      ),
    canActivate: [authGuard],
    children: [
      { path: '', pathMatch: 'full', canActivate: [defaultRedirectGuard], children: [] },
      { path: 'team', pathMatch: 'full', canActivate: [defaultRedirectGuard], children: [] },
      {
        path: 'maintenance',
        loadComponent: () =>
          import('./features/maintenance/maintenance.component').then(
            (m: {
              MaintenanceComponent: typeof import('./features/maintenance/maintenance.component').MaintenanceComponent;
            }) => m.MaintenanceComponent
          ),
      },
      {
        path: 'playground',
        canActivate: [routeAccessGuard(AppRoute.PLAYGROUND)],
        loadComponent: () =>
          import('./features/playground/playground.component').then(
            (m: {
              PlaygroundComponent: typeof import('./features/playground/playground.component').PlaygroundComponent;
            }) => m.PlaygroundComponent
          ),
      },
      {
        path: 'pre-game',
        canActivate: [maintenanceGuard, routeAccessGuard(AppRoute.PRE_GAME)],
        loadComponent: () =>
          import('./features/pre-game/pre-game-dashboard.component').then(
            (m: {
              PreGameDashboardComponent: typeof import('./features/pre-game/pre-game-dashboard.component').PreGameDashboardComponent;
            }) => m.PreGameDashboardComponent
          ),
      },
      {
        path: 'scouting',
        canActivate: [routeAccessGuard(AppRoute.SCOUTING)],
        loadComponent: () =>
          import('./features/scouting/scouting.component').then(
            (m: { ScoutingComponent: typeof import('./features/scouting/scouting.component').ScoutingComponent }) =>
              m.ScoutingComponent
          ),
      },
      {
        path: 'scouting-tactik',
        canActivate: [routeAccessGuard(AppRoute.SCOUTING_TACTIK)],
        loadComponent: () =>
          import('./features/scouting-tactik/scouting-tactik.component').then(
            (m: {
              ScoutingTactikComponent: typeof import('./features/scouting-tactik/scouting-tactik.component').ScoutingTactikComponent;
            }) => m.ScoutingTactikComponent
          ),
      },
      {
        path: 'commands',
        canActivate: [routeAccessGuard(AppRoute.COMMANDS)],
        loadComponent: () =>
          import('./features/commands/commands.component').then(
            (m: { CommandsComponent: typeof import('./features/commands/commands.component').CommandsComponent }) =>
              m.CommandsComponent
          ),
      },
      {
        path: 'game-report/:matchId',
        loadComponent: () =>
          import('./features/game-report/game-report.component').then(
            (m: {
              GameReportComponent: typeof import('./features/game-report/game-report.component').GameReportComponent;
            }) => m.GameReportComponent
          ),
      },
      {
        path: 'video-thumbnail-poc',
        loadComponent: () =>
          import('./features/video-poc/video-thumbnail/video-thumbnail.component').then(
            (m: {
              VideoThumbnailComponent: typeof import('./features/video-poc/video-thumbnail/video-thumbnail.component').VideoThumbnailComponent;
            }) => m.VideoThumbnailComponent
          ),
      },
      {
        path: 'unauthorized',
        loadComponent: () =>
          import('./features/error/unauthorized.component').then(
            (m: {
              UnauthorizedComponent: typeof import('./features/error/unauthorized.component').UnauthorizedComponent;
            }) => m.UnauthorizedComponent
          ),
      },
      {
        path: 'error',
        loadComponent: () =>
          import('./features/error/error-page-wrapper.component').then(
            (m: {
              ErrorPageWrapperComponent: typeof import('./features/error/error-page-wrapper.component').ErrorPageWrapperComponent;
            }) => m.ErrorPageWrapperComponent
          ),
      },
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('./layouts/full-layout.component').then(
        (m: { FullLayoutComponent: typeof import('./layouts/full-layout.component').FullLayoutComponent }) =>
          m.FullLayoutComponent
      ),
    children: [
      {
        path: 'login',
        canActivate: [publicGuard],
        loadComponent: () =>
          import('./features/auth/login/login.component').then(
            (m: { LoginComponent: typeof import('./features/auth/login/login.component').LoginComponent }) =>
              m.LoginComponent
          ),
      },
      {
        path: 'callback',
        loadComponent: () =>
          import('./features/auth/callback/callback.component').then(
            (m: {
              CallbackComponent: typeof import('./features/auth/callback/callback.component').CallbackComponent;
            }) => m.CallbackComponent
          ),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
